import React, { Suspense } from "react";
import { Provider } from "react-redux";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeProvider } from "@mui/material/styles";
import { store } from "./store";
import { theme } from "./const";
import { Buffer } from "buffer";
import "./index.css";
import "./public/locales/i18n";
import { LoaderProvider } from "./contexts/LoaderContext";
import { ErrorBoundary } from "react-error-boundary";
import { getFeatures, getSecrets } from "./config";
import { useTranslation } from "react-i18next";
import { ViewportProvider } from "./contexts";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { init as initApm } from "@elastic/apm-rum";
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Initialize RUM before any other code
const isActive = Math.random() < 0.05;

if (isActive) {
  var apm = initApm({
    serviceName: "dapp",
    serverUrl:
      "https://9a2962f168a84ffa92c1b5622233b6c7.apm.us-central1.gcp.cloud.es.io:443",
    serviceVersion: "",
    environment: process.env.NODE_ENV,
    active: true,
  });
}

const App = React.lazy(() => import("./pages/App"));
const IdentityProvider = React.lazy(() =>
  import("./contexts/IdentityContext").then((module) => ({
    default: module.IdentityProvider,
  }))
);
const RoomProvider = React.lazy(() =>
  import("./contexts/RoomContext").then((module) => ({
    default: module.RoomProvider,
  }))
);
const Web3AuthProvider = React.lazy(() =>
  import("./contexts/Web3AuthContext").then((module) => ({
    default: module.Web3AuthProvider,
  }))
);

const ENSProvider = React.lazy(() =>
  import("./contexts/ENSContext").then((module) => ({
    default: module.ENSProvider,
  }))
);
const BlockListProvider = React.lazy(() =>
  import("./contexts/blockListContext").then((module) => ({
    default: module.BlockListProvider,
  }))
);
const EasterEggProvider = React.lazy(() =>
  import("./contexts/EasterEggContext").then((module) => ({
    default: module.EasterEggProvider,
  }))
);
const WhiteboardWebsocketProvider = React.lazy(() =>
  import("./contexts/WhiteboardWebsocketContext").then((module) => ({
    default: module.WhiteboardWebsocketProvider,
  }))
);
const EventWebsocketProvider = React.lazy(() =>
  import("./contexts/EventWebsocketContext").then((module) => ({
    default: module.EventWebsocketProvider,
  }))
);
const DMWebsocketProvider = React.lazy(() =>
  import("./contexts/DMWebsocketContext").then((module) => ({
    default: module.DMWebsocketProvider,
  }))
);
const MarketplaceProvider = React.lazy(() =>
  import("./contexts/MarketplaceContext").then((module) => ({
    default: module.MarketplaceProvider,
  }))
);
const BtoCWebsocketProvider = React.lazy(() =>
  import("./contexts/BtoCWebsocketContext").then((module) => ({
    default: module.BtoCWebsocketProvider,
  }))
);
const { FEATURE_ERROR_RENDER_ENABLED } = getFeatures();
// @ts-ignore
window.Buffer = Buffer;

function Fallback({ error, resetErrorBoundary }: any) {
  const { i18n, t } = useTranslation();
  // Call resetErrorBoundary() to reset the error boundary and retry the render.
  return FEATURE_ERROR_RENDER_ENABLED ? (
    <div
      role="alert"
      style={{
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        color: "red",
      }}
    >
      <p style={{ width: "100%" }}>
        Something went wrong, please contact Protico team
      </p>
      <p
        style={{
          wordWrap: "break-word",
          width: "100%",
        }}
      >
        {error.message}
      </p>
    </div>
  ) : (
    <>
      <div
        className="forbid-App"
        style={{ textAlign: "center", height: "100vh", color: "white" }}
      >
        <p>{t("Alert_Lobby_System_Wrong")}</p>
        <p>{t("Alert_Wrong_Lobby_Info")}</p>
        <p>{t("Alert_Contact_Team")}</p>
      </div>
    </>
  );
}
const { GOOGLE_CLIENT_ID, STRIPE_PUBLISH_KEY } = getSecrets();
const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Suspense
      fallback={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            width: "100vw",
          }}
        >
          <div
            style={{
              flexBasis: "40px",
            }}
          />
          <div
            style={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <CircularProgress style={{ color: theme.brandColor }} size={20} />
          </div>
        </div>
      }
    >
      <ErrorBoundary FallbackComponent={Fallback}>
        <ViewportProvider>
          <Provider store={store}>
            <ThemeProvider theme={theme}>
              <BrowserRouter basename="protico">
                <LoaderProvider>
                  <IdentityProvider>
                    <RoomProvider>
                      <Web3AuthProvider>
                        <ENSProvider>
                          <BlockListProvider>
                            <EasterEggProvider>
                              <WhiteboardWebsocketProvider>
                                <EventWebsocketProvider>
                                  <DMWebsocketProvider>
                                    <BtoCWebsocketProvider>
                                      <MarketplaceProvider>
                                        <GoogleOAuthProvider
                                          clientId={GOOGLE_CLIENT_ID}
                                        >
                                              <Elements stripe={stripePromise}>
                                                                                          <App />
    </Elements>

                                        </GoogleOAuthProvider>
                                      </MarketplaceProvider>
                                    </BtoCWebsocketProvider>
                                  </DMWebsocketProvider>
                                </EventWebsocketProvider>
                              </WhiteboardWebsocketProvider>
                            </EasterEggProvider>
                          </BlockListProvider>
                        </ENSProvider>
                      </Web3AuthProvider>
                    </RoomProvider>
                  </IdentityProvider>
                </LoaderProvider>
              </BrowserRouter>
            </ThemeProvider>
          </Provider>
        </ViewportProvider>
      </ErrorBoundary>
    </Suspense>
  </React.StrictMode>
);
